// @flow
import type {Node} from 'react'
import React, {useState, useEffect, useCallback} from 'react'
import classNames from 'classnames'
import orgNavigation from './orgNavigation'
import subscribeToLocationChange from './subscribeToLocationChange'

import styles from './index.scss'

type Props = {
  activeClassName?: string,
  children: Node,
  className?: ?string,
  dataTurboFrame?: ?string,
  disabled?: boolean,
  highlightActive?: boolean,
  href: string,
  onClick?: ?(*) => any,
  target?: ?string,
  title?: string,
  disableSmartNavigation?: boolean,
  ...
}

const AppLink = ({
  activeClassName,
  children,
  dataTurboFrame,
  disabled = false,
  highlightActive = false,
  href,
  onClick,
  title,
  className,
  disableSmartNavigation,
  ...restLinkProps
}: Props): Node => {
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname)

  const handleLocationChange = useCallback(() => {
    setCurrentPathname(window.location.pathname)
  }, [])

  useEffect(() => subscribeToLocationChange(handleLocationChange), [handleLocationChange])

  const isActive = highlightActive && currentPathname === href

  const linkClassName = classNames(
    className || '',
    isActive && activeClassName && activeClassName,
  )

  const smartNavigate = useCallback((event: SyntheticMouseEvent<HTMLAnchorElement>) => {
    // Run the caller's handler first on every path, including React Router
    // navigation (GF-4042: the Jira setup steps save in onClick).
    if (onClick)
      onClick(event)

    if (event.isDefaultPrevented())
      return

    const shouldUseReactRouter = !disableSmartNavigation && orgNavigation.shouldUseReactRouter(href)

    if (shouldUseReactRouter) {
      const {currentTarget, button, metaKey, ctrlKey, shiftKey} = event
      const targetAttr = currentTarget.getAttribute('target') || ''
      const isAuxiliaryClick = typeof button === 'number' && button !== 0
      const isModifiedClick = metaKey || ctrlKey || shiftKey || isAuxiliaryClick
      const opensInNewContext = targetAttr && targetAttr.toLowerCase() !== '_self'

      if (!(isModifiedClick || opensInNewContext))
        orgNavigation.navigateViaReactRouter(href, () => event.preventDefault())
    }

    // Otherwise let the browser handle navigation (Turbo will pick it up)
  }, [href, onClick, disableSmartNavigation])

  if (disabled) {
    const buttonClass = classNames(styles.disabledButton, className)
    return (
      <button
        className={buttonClass}
        type="button"
        disabled
      >
        {children}
      </button>
    )
  }

  return (
    <a
      {...restLinkProps}
      className={linkClassName}
      href={href}
      onClick={smartNavigate}
      data-turbo
      data-turbo-frame={dataTurboFrame || 'page-frame'}
      title={title || ''}
    >
      {children}
    </a>
  )
}

export default AppLink
